import {
	Flex,
	Text,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	useDisclosure,
	Button,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import SettingsPagePersonnalInformationPanel from "./SettingsPagePersonnalInformationPanel";
import SettingsPageUsersPanel from "./SettingsPageUsersPanel";
import { useConnectedUser } from "../hooks/user.hooks";
import PersistingTabIndex from "../components/PersistingTabIndex";
import { userIsAdmin } from "../utils";
import { useCenters } from "../hooks/centers.hooks";
import SettingsPageUsersAdminPanel from "./SettingsPageUsersAdminPanel";
import SettingsPagePersonnalInformationAdminPanel from "./SettingsPagePersonnalInformationAdminPanel";
import SettingsPagePrinterPanel from "./SettingsPagePrinterPanel";
import Card from "../components/Card";
import SimpleModal from "../components/modals/SimpleModal";
import AdminSettingsForm, {
	type AdminSettingsFormHandler,
} from "../components/forms/AdminSettingsForm";
import { useAdminSettings } from "../hooks/admin_settings.hooks";
import { useRef } from "react";
import { updateAdminSettingsMutation } from "../mutations/admin_settings.mutations";
import { useMutation, useQueryClient } from "react-query";
import SettingsPageAnnouncementPanel from "./SettingsPageAnnouncementPanel";

const SettingsPageAdminPanel = () => {
	const adminEditionDisclosure = useDisclosure();
	const adminEditionRef = useRef<AdminSettingsFormHandler>(null);
	const { data: adminSettings } = useAdminSettings();
	const queryClient = useQueryClient();
	const { mutate: updateAdminSettings } = useMutation(
		updateAdminSettingsMutation,
		{
			onSuccess: () => {
				adminEditionDisclosure.onClose();
				queryClient.invalidateQueries("admin-settings");
			},
		},
	);

	const handleClick = async () => {
		const current = adminEditionRef.current;
		if (current) {
			const valid = await current.valid();
			if (valid) {
				const values = current.values();
				updateAdminSettings({ settings: values });
			}
		}
	};
	return (
		<>
			<Card w="768px" px={4} py={2}>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Remise refonte</Text>
					<Text>
						{adminSettings?.baseDiscountPercentage
							? adminSettings?.baseDiscountPercentage * 100
							: 0}
						{" %"}
					</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>UPS (Sage ID)</Text>
					<Text>{adminSettings?.upsSageId}</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>UPS (Prix de livraison)</Text>
					<Text>
						{adminSettings?.upsShippingPrice
							? adminSettings.upsShippingPrice / 100
							: 0}
					</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Express (Sage ID)</Text>
					<Text>{adminSettings?.expressSageId}</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Express (Prix)</Text>
					<Text>
						{adminSettings?.expressPrice ? adminSettings.expressPrice / 100 : 0}
					</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Temps de production moyen</Text>
					<Text>
						{adminSettings?.meanDaysProductionTime}
						{" jrs"}
					</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Temps de production moyen en mode prioritaire</Text>
					<Text>
						{adminSettings?.meanDaysExpressProductionTime}
						{" jrs"}
					</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Temps de réception moyen (physique)</Text>
					<Text>
						{adminSettings?.meanDaysReceiptTime}
						{" jrs"}
					</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Temps d'expédition moyen</Text>
					<Text>
						{adminSettings?.meanDaysShippingTime}
						{" jrs"}
					</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Création de commandes</Text>
					<Text>
						{adminSettings?.disableCommandCreation ? "Désactivée" : "Activée"}
					</Text>
				</Flex>
				<Flex
					borderBottom="solid 1px"
					py={2}
					borderColor="gray.200"
					justifyContent="space-between"
				>
					<Text>Adresse service impression</Text>
					<Text>{adminSettings?.printingServiceAddress}</Text>
				</Flex>

				<Flex justifyContent="end" pt={4}>
					<Button onClick={() => adminEditionDisclosure.onOpen()}>
						Modifier
					</Button>
				</Flex>
			</Card>

			<SimpleModal
				acceptText="Valider"
				onClose={adminEditionDisclosure.onClose}
				isOpen={adminEditionDisclosure.isOpen}
				title="Modifier les paramètres d'administration"
				onClick={handleClick}
			>
				<AdminSettingsForm ref={adminEditionRef} value={adminSettings} />
			</SimpleModal>
		</>
	);
};

function SettingsPage() {
	const user = useConnectedUser();
	const { center } = useCenters();

	if (user?.isSuperAdmin === false && center === null) {
		return <></>;
	}
	return (
		<>
			<PageTitle pb={4}>
				{user?.isSuperAdmin === true && <>Paramètres</>}
				{user?.isSuperAdmin !== true && <>{center?.name}: Paramètres</>}
			</PageTitle>

			<PersistingTabIndex id={"contact"}>
				<TabList>
					<Tab
						_selected={{
							color: "brand.700",
							borderColor: "brand.700",
						}}
					>
						Informations personnelles
					</Tab>
					<Tab
						_selected={{
							color: "brand.700",
							borderColor: "brand.700",
						}}
					>
						Utilisateurs
					</Tab>
					{userIsAdmin(user) && (
						<Tab
							_selected={{
								color: "brand.700",
								borderColor: "brand.700",
							}}
						>
							Imprimantes
						</Tab>
					)}
					{userIsAdmin(user) && (
						<Tab
							_selected={{
								color: "brand.700",
								borderColor: "brand.700",
							}}
						>
							Contenus
						</Tab>
					)}
					{userIsAdmin(user) && (
						<Tab
							_selected={{
								color: "brand.700",
								borderColor: "brand.700",
							}}
						>
							Admin
						</Tab>
					)}
				</TabList>

				<TabPanels>
					<TabPanel px={0}>
						{userIsAdmin(user) === true && (
							<SettingsPagePersonnalInformationAdminPanel></SettingsPagePersonnalInformationAdminPanel>
						)}
						{userIsAdmin(user) !== true && (
							<SettingsPagePersonnalInformationPanel></SettingsPagePersonnalInformationPanel>
						)}
					</TabPanel>
					<TabPanel px={0}>
						{userIsAdmin(user) !== true && <SettingsPageUsersPanel />}
						{userIsAdmin(user) === true && <SettingsPageUsersAdminPanel />}
					</TabPanel>
					{userIsAdmin(user) && (
						<TabPanel px={0}>
							<SettingsPagePrinterPanel></SettingsPagePrinterPanel>
						</TabPanel>
					)}
					{userIsAdmin(user) && (
						<TabPanel px={0}>
							<SettingsPageAnnouncementPanel />
						</TabPanel>
					)}
					{userIsAdmin(user) && (
						<TabPanel px={0}>
							<SettingsPageAdminPanel />
						</TabPanel>
					)}
				</TabPanels>
			</PersistingTabIndex>
		</>
	);
}

export default SettingsPage;
